import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useDefaultTasksApi } from "../../../http/defaultTasks/api";
import { UpsertDefaultTask } from "../../../http/defaultTasks/types";
import ModalDialog from "../../ui/ModalDialog";
import DefaultTaskForm, { DefaultTaskFormBody } from "./DefaultTaskForm";

type EditDefaultTaskDialogProps = {
  taskId: string;
  onClose: () => void;
};

const EditDefaultTaskDialog: FC<EditDefaultTaskDialogProps> = ({ taskId, onClose }: EditDefaultTaskDialogProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tasksApi = useDefaultTasksApi();

  const defaultTaskSwr = tasksApi.getAdimnDefaultTask(taskId, aborter.current.signal);
  const [defaultTask, isInitialized] = useDataInitializer(defaultTaskSwr.data, defaultTaskSwr.error);

  useEffect(() => {
    taskId && defaultTaskSwr.trigger();
  }, [taskId]);

  useEffect(() => {
    defaultTaskSwr.error && dispatch(showErrorNotification(i18n, defaultTaskSwr.error));
  }, [defaultTaskSwr.error]);

  const onSubmit = async (formData: DefaultTaskFormBody): Promise<void> => {
    if (defaultTask?.id) {
      setIsSubmitting(true);

      const body: UpsertDefaultTask = {
        name: formData.name,
        categories: formData.categories,
        defaultDueDateTypeId: formData.defaultDueDateType?.id,
      };

      try {
        await tasksApi.updateAdminDefaultTask(defaultTask.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={() => onClose()} title={i18n.t.defaultTasks.edit}>
      <DefaultTaskForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.defaultTask.update || isSubmitting || !defaultTask?.id}
        isLoading={!isInitialized}
        task={defaultTask}
      />
    </ModalDialog>
  );
};

export default EditDefaultTaskDialog;
